import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../components/AuthContext";
import {
  getAllFeedback,
  updateFeedbackStatus,
  getFeedbackStats,
} from "../api/feedback";
import toast from "react-hot-toast";
import {
  Search,
  Filter,
  Star,
  Bug,
  Lightbulb,
  MessageSquare,
  CheckCircle,
  Clock,
  XCircle,
  Calendar,
  User,
  Image as ImageIcon,
} from "lucide-react";

const FeedbackDashboard = () => {
  const { adminToken } = useAuth();
  const [feedback, setFeedback] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    type: "",
    status: "",
    rating: "",
    search: "",
    page: 1,
    limit: 20,
    sortBy: "createdAt",
    sortOrder: "desc",
  });
  const [pagination, setPagination] = useState({});
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const fetchFeedback = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAllFeedback(adminToken, filters);
      if (response.success) {
        setFeedback(response.feedback);
        setPagination(response.pagination);
      }
    } catch (error) {
      console.error("Error fetching feedback:", error);
      toast.error("Failed to load feedback");
    } finally {
      setLoading(false);
    }
  }, [adminToken, filters]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await getFeedbackStats(adminToken);
      if (response.success) {
        setStats(response.stats);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, [adminToken]);

  useEffect(() => {
    if (adminToken) {
      fetchFeedback();
      fetchStats();
    }
  }, [adminToken, filters, fetchFeedback, fetchStats]);

  const handleStatusUpdate = async (id, newStatus, adminNotes = "") => {
    try {
      const response = await updateFeedbackStatus(
        id,
        { status: newStatus, adminNotes },
        adminToken
      );
      if (response.success) {
        toast.success(`Feedback marked as ${newStatus}`);
        fetchFeedback();
        fetchStats();
        setSelectedFeedback(null);
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update feedback status");
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: 1, // Reset to first page when filter changes
    }));
  };

  const clearFilters = () => {
    setFilters({
      type: "",
      status: "",
      rating: "",
      search: "",
      page: 1,
      limit: 20,
      sortBy: "createdAt",
      sortOrder: "desc",
    });
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "Bug":
        return <Bug className="w-5 h-5 text-red-500" />;
      case "Suggestion":
        return <Lightbulb className="w-5 h-5 text-yellow-500" />;
      default:
        return <MessageSquare className="w-5 h-5 text-red-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      Pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400",
      Resolved: "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400",
      "In Progress": "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400",
    };

    const icons = {
      Pending: <Clock className="w-4 h-4" />,
      Resolved: <CheckCircle className="w-4 h-4" />,
      "In Progress": <Clock className="w-4 h-4" />,
    };

    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}
      >
        {icons[status]}
        {status}
      </span>
    );
  };

  return (
    <div className="w-full overflow-x-hidden">
      <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-red-600 to-red-700 dark:from-red-400 dark:to-red-500 bg-clip-text text-transparent mb-6 sm:mb-8 break-words">
        Feedback Dashboard
      </h1>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div className="backdrop-blur-xl bg-white/60 dark:bg-gray-800/60 rounded-2xl border border-white/20 dark:border-gray-700/30 shadow-xl shadow-black/5 p-6 hover:shadow-2xl hover:shadow-black/10 transition-all duration-300 hover:scale-[1.02]">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 font-medium truncate">Total Feedback</p>
                <p className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-red-600 to-red-700 dark:from-red-400 dark:to-red-500 bg-clip-text text-transparent break-words">
                  {stats.total}
                </p>
              </div>
              <div className="p-3 backdrop-blur-sm bg-red-500/20 dark:bg-red-600/20 rounded-xl">
                <MessageSquare className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
            </div>
          </div>

          <div className="backdrop-blur-xl bg-white/60 dark:bg-gray-800/60 rounded-2xl border border-white/20 dark:border-gray-700/30 shadow-xl shadow-black/5 p-6 hover:shadow-2xl hover:shadow-black/10 transition-all duration-300 hover:scale-[1.02]">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 font-medium truncate">Pending</p>
                <p className="text-xl sm:text-2xl font-bold text-yellow-600 dark:text-yellow-400 break-words">{stats.pending}</p>
              </div>
              <div className="p-3 backdrop-blur-sm bg-yellow-500/20 dark:bg-yellow-600/20 rounded-xl">
                <Clock className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
              </div>
            </div>
          </div>

          <div className="backdrop-blur-xl bg-white/60 dark:bg-gray-800/60 rounded-2xl border border-white/20 dark:border-gray-700/30 shadow-xl shadow-black/5 p-6 hover:shadow-2xl hover:shadow-black/10 transition-all duration-300 hover:scale-[1.02]">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 font-medium truncate">Resolved</p>
                <p className="text-xl sm:text-2xl font-bold text-green-600 dark:text-green-400 break-words">{stats.resolved}</p>
              </div>
              <div className="p-3 backdrop-blur-sm bg-green-500/20 dark:bg-green-600/20 rounded-xl">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>

          <div className="backdrop-blur-xl bg-white/60 dark:bg-gray-800/60 rounded-2xl border border-white/20 dark:border-gray-700/30 shadow-xl shadow-black/5 p-6 hover:shadow-2xl hover:shadow-black/10 transition-all duration-300 hover:scale-[1.02]">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 font-medium truncate">Avg Rating</p>
                <p className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-yellow-500 to-orange-500 dark:from-yellow-400 dark:to-orange-400 bg-clip-text text-transparent break-words">
                  {stats.averageRating}/5
                </p>
              </div>
              <div className="p-3 backdrop-blur-sm bg-yellow-500/20 dark:bg-yellow-600/20 rounded-xl">
                <Star className="w-8 h-8 text-yellow-600 dark:text-yellow-400 fill-yellow-400 dark:fill-yellow-500" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="backdrop-blur-xl bg-white/60 dark:bg-gray-800/60 rounded-2xl border border-white/20 dark:border-gray-700/30 shadow-xl shadow-black/5 p-4 sm:p-6 mb-4 sm:mb-6 overflow-x-hidden">
        <div className="flex flex-col md:flex-row gap-3 sm:gap-4 mb-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search feedback..."
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 backdrop-blur-sm bg-white/50 dark:bg-gray-700/50 border border-white/20 dark:border-gray-600/30 rounded-xl focus:ring-2 focus:ring-red-500/50 dark:focus:ring-red-400/50 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-300 shadow-lg shadow-black/5"
              />
            </div>
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-4 py-2.5 backdrop-blur-sm bg-white/50 dark:bg-gray-700/50 border border-white/20 dark:border-gray-600/30 rounded-xl hover:bg-white/70 dark:hover:bg-gray-700/70 flex items-center gap-2 transition-all duration-300 shadow-lg shadow-black/5 hover:shadow-xl"
          >
            <Filter className="w-5 h-5" />
            Filters
          </button>
        </div>

        {/* Filter Options */}
        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 pt-4 border-t border-white/20 dark:border-gray-700/30">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Type
              </label>
              <select
                value={filters.type}
                onChange={(e) => handleFilterChange("type", e.target.value)}
                className="w-full px-3 py-2.5 backdrop-blur-sm bg-white/50 dark:bg-gray-700/50 border border-white/20 dark:border-gray-600/30 rounded-xl dark:text-white transition-all duration-300 shadow-lg shadow-black/5"
              >
                <option value="">All Types</option>
                <option value="Bug">Bug</option>
                <option value="Suggestion">Suggestion</option>
                <option value="Feedback">Feedback</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
                className="w-full px-3 py-2.5 backdrop-blur-sm bg-white/50 dark:bg-gray-700/50 border border-white/20 dark:border-gray-600/30 rounded-xl dark:text-white transition-all duration-300 shadow-lg shadow-black/5"
              >
                <option value="">All Status</option>
                <option value="Pending">Pending</option>
                <option value="In Progress">In Progress</option>
                <option value="Resolved">Resolved</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Rating
              </label>
              <select
                value={filters.rating}
                onChange={(e) => handleFilterChange("rating", e.target.value)}
                className="w-full px-3 py-2.5 backdrop-blur-sm bg-white/50 dark:bg-gray-700/50 border border-white/20 dark:border-gray-600/30 rounded-xl dark:text-white transition-all duration-300 shadow-lg shadow-black/5"
              >
                <option value="">All Ratings</option>
                <option value="5">5 Stars</option>
                <option value="4">4 Stars</option>
                <option value="3">3 Stars</option>
                <option value="2">2 Stars</option>
                <option value="1">1 Star</option>
                <option value="none">No Rating</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Sort By
              </label>
              <select
                value={`${filters.sortBy}-${filters.sortOrder}`}
                onChange={(e) => {
                  const [sortBy, sortOrder] = e.target.value.split("-");
                  handleFilterChange("sortBy", sortBy);
                  handleFilterChange("sortOrder", sortOrder);
                }}
                className="w-full px-3 py-2.5 backdrop-blur-sm bg-white/50 dark:bg-gray-700/50 border border-white/20 dark:border-gray-600/30 rounded-xl dark:text-white transition-all duration-300 shadow-lg shadow-black/5"
              >
                <option value="createdAt-desc">Newest First</option>
                <option value="createdAt-asc">Oldest First</option>
                <option value="rating-desc">Highest Rating</option>
                <option value="rating-asc">Lowest Rating</option>
              </select>
            </div>

            <div className="md:col-span-4">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-sm backdrop-blur-sm bg-white/50 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 rounded-xl transition-all duration-300 border border-white/20 dark:border-gray-600/30 hover:bg-white/70 dark:hover:bg-gray-700/70"
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Feedback List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading feedback...</p>
        </div>
      ) : feedback.length === 0 ? (
        <div className="backdrop-blur-xl bg-white/60 dark:bg-gray-800/60 rounded-2xl border border-white/20 dark:border-gray-700/30 shadow-xl shadow-black/5 p-12 text-center">
          <MessageSquare className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-300">No feedback found</p>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {feedback.map((item) => (
              <div
                key={item._id}
                className="backdrop-blur-xl bg-white/60 dark:bg-gray-800/60 rounded-2xl border border-white/20 dark:border-gray-700/30 shadow-xl shadow-black/5 p-6 hover:shadow-2xl hover:shadow-black/10 transition-all duration-300 cursor-pointer hover:scale-[1.01]"
                onClick={() => setSelectedFeedback(item)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {getTypeIcon(item.type)}
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {item.type}
                      </span>
                      {getStatusBadge(item.status)}
                    </div>

                    <p className="text-gray-700 dark:text-gray-300 mb-3 line-clamp-2 break-words overflow-hidden">
                      {item.message}
                    </p>

                    {/* Screenshot Thumbnail Preview */}
                    {item.screenshotUrl && (
                      <div className="mb-3">
                        <div className="relative group">
                          <img
                            src={item.screenshotUrl}
                            alt="Screenshot preview"
                            className="w-full max-w-full sm:max-w-xs h-32 object-cover rounded-lg border border-white/20 dark:border-gray-700/30 cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedFeedback(item);
                            }}
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 rounded-lg transition-colors flex items-center justify-center">
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                              <ImageIcon className="w-6 h-6 text-white drop-shadow-lg" />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                      {item.rating ? (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          <span className="whitespace-nowrap">{item.rating}/5</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Star className="w-4 h-4 text-gray-300" />
                          <span className="text-gray-400 whitespace-nowrap">No rating</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Calendar className="w-4 h-4" />
                        <span className="whitespace-nowrap">
                          {new Date(item.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      {(item.userId || item.contactName) && (
                        <div className="flex items-center gap-1 min-w-0">
                          <User className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate max-w-[150px] sm:max-w-none">{item.userId?.name || item.contactName || "User"}</span>
                          {!item.userId && item.contactName && (
                            <span className="text-xs text-gray-400 flex-shrink-0">(Guest)</span>
                          )}
                        </div>
                      )}
                      {item.screenshotUrl && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <ImageIcon className="w-4 h-4 text-red-500" />
                          <span className="text-red-600 dark:text-red-400 font-medium whitespace-nowrap hidden sm:inline">Screenshot Available</span>
                          <span className="text-red-600 dark:text-red-400 font-medium whitespace-nowrap sm:hidden">Screenshot</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex flex-wrap items-center justify-center gap-2 mt-6 sm:mt-8">
              <button
                onClick={() => handleFilterChange("page", filters.page - 1)}
                disabled={filters.page === 1}
                className="px-3 sm:px-4 py-2 backdrop-blur-sm bg-white/50 dark:bg-gray-700/50 border border-white/20 dark:border-gray-600/30 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/70 dark:hover:bg-gray-700/70 transition-all duration-300 shadow-lg shadow-black/5 text-sm sm:text-base whitespace-nowrap"
              >
                Previous
              </button>
              <span className="px-3 sm:px-4 py-2 backdrop-blur-sm bg-white/50 dark:bg-gray-700/50 rounded-xl border border-white/20 dark:border-gray-600/30 shadow-lg shadow-black/5 text-sm sm:text-base whitespace-nowrap">
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                onClick={() => handleFilterChange("page", filters.page + 1)}
                disabled={filters.page >= pagination.pages}
                className="px-3 sm:px-4 py-2 backdrop-blur-sm bg-white/50 dark:bg-gray-700/50 border border-white/20 dark:border-gray-600/30 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/70 dark:hover:bg-gray-700/70 transition-all duration-300 shadow-lg shadow-black/5 text-sm sm:text-base whitespace-nowrap"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Feedback Detail Modal */}
      {selectedFeedback && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-3 sm:px-4 py-4">
            <div
              className="fixed inset-0 backdrop-blur-sm bg-black/40 dark:bg-black/60"
              onClick={() => setSelectedFeedback(null)}
            ></div>
            <div className="relative backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-3xl border border-white/20 dark:border-gray-700/30 shadow-2xl shadow-black/20 max-w-3xl w-full p-4 sm:p-6 lg:p-8 overflow-x-hidden">
              <div className="flex items-start justify-between mb-4 gap-3">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                  {getTypeIcon(selectedFeedback.type)}
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white truncate">
                    {selectedFeedback.type}
                  </h2>
                  <div className="flex-shrink-0">
                    {getStatusBadge(selectedFeedback.status)}
                  </div>
                </div>
                <button
                  onClick={() => setSelectedFeedback(null)}
                  className="p-2 backdrop-blur-sm bg-white/50 dark:bg-gray-700/50 rounded-xl hover:bg-white/70 dark:hover:bg-gray-700/70 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-all duration-300 border border-white/20 dark:border-gray-600/30"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Rating
                  </label>
                  {selectedFeedback.rating ? (
                    <div className="flex items-center gap-1 mt-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`w-5 h-5 ${
                            star <= selectedFeedback.rating
                              ? "fill-yellow-400 text-yellow-400"
                              : "text-gray-300"
                          }`}
                        />
                      ))}
                      <span className="ml-2 text-gray-700 dark:text-gray-300">
                        {selectedFeedback.rating}/5
                      </span>
                    </div>
                  ) : (
                    <p className="mt-1 text-gray-500 dark:text-gray-400">No rating provided</p>
                  )}
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Message
                  </label>
                  <p className="mt-1 text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words overflow-wrap-anywhere">
                    {selectedFeedback.message}
                  </p>
                </div>

                {selectedFeedback.screenshotUrl && (
                  <div>
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2 block">
                      Screenshot
                    </label>
                    <div className="relative group">
                      <img
                        src={selectedFeedback.screenshotUrl}
                        alt="Screenshot"
                        className="mt-2 max-w-full h-auto rounded-xl border-2 border-white/20 dark:border-gray-700/30 shadow-xl cursor-pointer hover:scale-[1.02] transition-transform duration-300"
                        onClick={() => {
                          // Open screenshot in new tab for full view
                          window.open(selectedFeedback.screenshotUrl, '_blank');
                        }}
                      />
                      <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="backdrop-blur-sm bg-black/50 text-white px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-2">
                          <ImageIcon className="w-4 h-4" />
                          Click to view full size
                        </div>
                      </div>
                    </div>
                    <div className="mt-2">
                      <a
                        href={selectedFeedback.screenshotUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
                      >
                        <ImageIcon className="w-4 h-4" />
                        Open in new tab
                      </a>
                    </div>
                  </div>
                )}

                {(selectedFeedback.userId || selectedFeedback.contactName || selectedFeedback.contactEmail) && (
                  <div>
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Contact Information
                    </label>
                    {selectedFeedback.userId ? (
                      <p className="mt-1 text-gray-700 dark:text-gray-300 break-words">
                        <span className="break-words">{selectedFeedback.userId.name}</span> 
                        <span className="break-all"> ({selectedFeedback.userId.email})</span>
                        <span className="ml-2 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">(Registered User)</span>
                      </p>
                    ) : (
                      <div className="mt-1 space-y-1">
                        {selectedFeedback.contactName && (
                          <p className="text-gray-700 dark:text-gray-300 break-words">
                            <span className="font-medium">Name:</span> <span className="break-words">{selectedFeedback.contactName}</span>
                          </p>
                        )}
                        {selectedFeedback.contactEmail && (
                          <p className="text-gray-700 dark:text-gray-300 break-words">
                            <span className="font-medium">Email:</span> <span className="break-all">{selectedFeedback.contactEmail}</span>
                            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">(Guest)</span>
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {selectedFeedback.deviceInfo && (
                  <div>
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Device Info
                    </label>
                    <div className="mt-1 text-sm text-gray-600 dark:text-gray-400 space-y-1">
                      <p>OS: {selectedFeedback.deviceInfo.os}</p>
                      <p>Browser: {selectedFeedback.deviceInfo.browser}</p>
                      <p>Screen: {selectedFeedback.deviceInfo.screenResolution}</p>
                    </div>
                  </div>
                )}

                {selectedFeedback.adminNotes && (
                  <div>
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Admin Notes
                    </label>
                    <p className="mt-1 text-gray-700 dark:text-gray-300">
                      {selectedFeedback.adminNotes}
                    </p>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 sm:gap-3 pt-4 border-t border-white/20 dark:border-gray-700/30">
                  {selectedFeedback.status !== "Pending" && (
                    <button
                      onClick={() =>
                        handleStatusUpdate(selectedFeedback._id, "Pending")
                      }
                      className="px-3 sm:px-4 py-2 sm:py-2.5 backdrop-blur-sm bg-yellow-500/80 dark:bg-yellow-600/80 text-white rounded-xl hover:bg-yellow-600 dark:hover:bg-yellow-700 transition-all duration-300 shadow-lg shadow-yellow-500/30 hover:shadow-xl hover:shadow-yellow-500/40 hover:scale-[1.02] text-sm sm:text-base whitespace-nowrap"
                    >
                      Mark as Pending
                    </button>
                  )}
                  {selectedFeedback.status !== "In Progress" && (
                    <button
                      onClick={() =>
                        handleStatusUpdate(selectedFeedback._id, "In Progress")
                      }
                      className="px-3 sm:px-4 py-2 sm:py-2.5 backdrop-blur-sm bg-red-500/80 dark:bg-red-600/80 text-white rounded-xl hover:bg-red-600 dark:hover:bg-red-700 transition-all duration-300 shadow-lg shadow-red-500/30 hover:shadow-xl hover:shadow-red-500/40 hover:scale-[1.02] text-sm sm:text-base whitespace-nowrap"
                    >
                      Mark as In Progress
                    </button>
                  )}
                  {selectedFeedback.status !== "Resolved" && (
                    <button
                      onClick={() =>
                        handleStatusUpdate(selectedFeedback._id, "Resolved")
                      }
                      className="px-3 sm:px-4 py-2 sm:py-2.5 backdrop-blur-sm bg-green-500/80 dark:bg-green-600/80 text-white rounded-xl hover:bg-green-600 dark:hover:bg-green-700 transition-all duration-300 shadow-lg shadow-green-500/30 hover:shadow-xl hover:shadow-green-500/40 hover:scale-[1.02] text-sm sm:text-base whitespace-nowrap"
                    >
                      Mark as Resolved
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackDashboard;

